import { cardCount, selectableFamilies } from "@/content/families"
import type { SessionConfig } from "@/types"

/*
 * Every label, hint and summary in the lobby's rules panel, and the tables the
 * controls are generated from. Kept out of the components so the panel is
 * layout, and so copy changes cannot break a test that reads a class name.
 */

type StepperKey = "painsPerParticipant" | "votesPerParticipant" | "handCap" | "mustHaveCap"

export interface StepperSpec {
  key: StepperKey
  label: string
  hint: string
  min: number
  max: number
}

const STEPPERS: readonly StepperSpec[] = [
  {
    key: "painsPerParticipant",
    label: "Notes per person",
    hint: "Problems each person writes before the vote",
    min: 1,
    max: 6,
  },
  {
    key: "votesPerParticipant",
    label: "Votes per person",
    hint: "Dots each person spends picking the top problem",
    min: 1,
    max: 5,
  },
  // Its hint depends on the value, so it is written in `stepperHint`.
  { key: "handCap", label: "Cards per person", hint: "", min: 0, max: 12 },
  {
    key: "mustHaveCap",
    label: "Must-have slots on the board",
    hint: "Forces the group to choose what really matters",
    min: 1,
    max: 6,
  },
]

type ExerciseKey = "painsEnabled" | "userStoryEnabled" | "deepDiveEnabled"

export interface ExerciseSpec {
  key: ExerciseKey
  label: string
  hint: string
}

/*
 * Naming the job, picking cards and setting priorities are not here: every pick,
 * placement and deep-dive answer hangs off a job, so a session without one
 * collects nothing. Only the warm-up and the deep dive can be dropped.
 */
const EXERCISES: readonly ExerciseSpec[] = [
  {
    key: "painsEnabled",
    label: "Pain points & vote",
    hint: "The room writes and votes before the job is named",
  },
  { key: "userStoryEnabled", label: "User-story format", hint: "As a … I want … so that …" },
  {
    key: "deepDiveEnabled",
    label: "Deep dive round",
    hint: "Four boxes per use case, after the board",
  },
]

export const RULES_COPY = {
  heading: "Session rules",
  template: "Template",
  sessionLength: (minutes: number) => `Session length ~${minutes} min`,
  rounds: "Rounds & limits",
  exercises: "Exercises",
  families: "Card families",
  knownPains: "Known pain points",
  painsHint: "Displayed to participants as examples. Use them as conversation starters.",
  noExercises: "All optional exercises off",
  noFamilies: "No families selected",
  /* Said where the deck cannot be emptied — mid-session, where a deal with no
   * families leaves every participant swiping an empty deck. */
  lastFamily: "The room is playing this deck. At least one family has to stay in.",
} as const

/*
 * Pain points and voting are one round. With it off, its two limits describe
 * nothing, and so do the exercises that read its output — the artifact drops
 * them from the panel rather than dimming them.
 */
export function steppersFor(config: SessionConfig): readonly StepperSpec[] {
  if (config.painsEnabled) return STEPPERS
  return STEPPERS.filter((s) => s.key !== "painsPerParticipant" && s.key !== "votesPerParticipant")
}

/*
 * Every exercise applies to every session now, so there is nothing to filter and
 * nothing to render as inert: the job is always written, so the user-story
 * format always has something to apply to.
 */
export function exercisesFor(): readonly ExerciseSpec[] {
  return EXERCISES
}

export function stepperHint(stepper: StepperSpec, config: SessionConfig): string {
  if (stepper.key !== "handCap") return stepper.hint
  return config.handCap === 0
    ? "No limit — the cards round runs longer"
    : "Fewer cards keeps the cards round short"
}

/** Zero cards means no limit, which reads as a number otherwise. */
export function handCapFormat(value: number): string {
  return value === 0 ? "∞" : String(value)
}

export function roundsSummary(config: SessionConfig): string {
  return [
    config.painsEnabled ? `${config.painsPerParticipant} pains each` : null,
    config.painsEnabled ? `${config.votesPerParticipant} votes` : null,
    config.handCap === 0 ? "no card limit" : `${config.handCap} cards in hand`,
    `${config.mustHaveCap} must-have slots`,
  ]
    .filter(Boolean)
    .join(" · ")
}

export function exercisesSummary(config: SessionConfig): string {
  return (
    exercisesFor()
      .filter((exercise) => config[exercise.key])
      .map((exercise) => exercise.label.toLowerCase())
      .join(" · ") || RULES_COPY.noExercises
  )
}

export function familiesSummary(config: SessionConfig): string {
  if (config.families.length === 0) return RULES_COPY.noFamilies
  // Count against what the picker offers (active families, plus any retired one
  // this session already plays), never the raw table — see `selectableFamilies`.
  const total = selectableFamilies(config.families).length
  if (config.families.length === total) return `All ${total} families`
  return `${config.families.length} of ${total} families`
}

export function cardsSummary(config: SessionConfig): string {
  return `${cardCount(config.families)} cards`
}

/** Blank notes are kept in the array so one being typed into does not vanish;
 * they do not count towards what the room will see. */
export function loadedPains(notes: string[]): number {
  return notes.filter((note) => note.trim()).length
}

export function painsLabel(loaded: number): string {
  return loaded === 0 ? "Nothing loaded yet" : `${loaded} on the board already`
}

export function painsCountLine(loaded: number): string {
  if (loaded === 0) return "No pain points yet"
  return `${loaded} ${loaded === 1 ? "pain point" : "pain points"} on the board`
}

export function painsActionLine(loaded: number): string {
  return loaded === 0 ? "Add what the client told you" : "Review or add more"
}
